import React, { Fragment, useEffect, useState } from 'react'
import { Modal } from '../../../components/_EOS/Modal'
import { Size, useModal } from '../../../hooks/useModal'
import {
  patchProductOrigin,
  postProductOrigin,
  getTypeOperationWarehouse
} from '../api'
import {
  useOrderOrigins,
  useProductOrigins,
  useWarehouses
} from '../../../hooks'
import {
  ProductOrigin,
  SelectedWarehousesByOperation
} from '../../../interfaces/Producto/ProductOrigins'
import { Field, Form, Formik } from 'formik'
import { CheckboxWithLabel, TextField } from 'formik-mui'
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Slider,
  Typography
} from '@mui/material'
import { productOriginValidationSchema } from '../Config/config'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'
import { TypeStorageDLL } from '../../../components/TypeStorage/TypeStorageDLL'
import { GenericMultiselectDLL } from '../../../components/Almacen/UI/GenericMultiselectDLL'
import { OrderOrigin } from '../../../interfaces/OrderOrigin'
import { TypeOperationWarehousesTable } from '../../../components/TypeOperation/TypeOperationWarehouse'
import { TypeOperation } from '../../../interfaces/TypeOperation/TypeOperation'

interface ProductOriginModalProps {
  isOpen: boolean
  size: Size
  title: string
  extendedProps?: ProductOrigin
  closeModal: () => void
  handleCloseModalMessage: (succes: boolean, message: string) => void
  editMode: boolean
}

const initialValues: ProductOrigin = {
  productOriginId: 0,
  origin: '',
  name: '',
  externalStorage: false,
  typeStorageId: 1,
  orderOriginIds: [],
  overloadPercentage: 0,
  hasPriorityCourier: false,
  hasPriorityOverload: false,
  hasPrioritySingleSku: false,
  hasExpirationDays: false,
  expirationDays: 0,
  warehousesByOperation: []
}

export const ProductOriginModal: React.FC<ProductOriginModalProps> = ({
  isOpen,
  closeModal,
  size,
  extendedProps,
  handleCloseModalMessage,
  editMode
}) => {
  const {
    callApi: patchOriginCallApi,
    state: { status }
  } = useApi()

  const { callApi: getTypeOperationCallApi, state: stateTypeOperation } =
    useApi<TypeOperation[]>()

  const {
    size: sizeModalConfirm,
    isOpen: isOpenModalConfirm,
    openModal: openModalConfirm,
    closeModal: closeModalConfirm
  } = useModal('')

  const { getProductOrigins } = useProductOrigins()

  const {
    getOrderOrigins,
    orderOriginsState: { orderOrigins }
  } = useOrderOrigins()

  useEffect(() => {
    if (orderOrigins?.length === 0) {
      getOrderOrigins()
    }
  }, [])

  const {
    getWarehouses,
    warehousesState: { warehouses }
  } = useWarehouses()

  useEffect(() => {
    if (warehouses?.length === 0) {
      getWarehouses()
    }
  }, [])

  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const [selectedTypePriorization, setSelectedTypePriorization] = useState<
    number[]
  >([])
  const [overloadPercentage, setOverloadPercentage] = useState(0)

  const [selectedWarehousesByOperation, setSelectedWarehousesByOperation] =
    useState<SelectedWarehousesByOperation[]>()

  const requestGetTypeOperation = (
    typeStorageId: number,
    productOriginId?: number
  ) => {
    getTypeOperationCallApi(
      getTypeOperationWarehouse(typeStorageId, productOriginId)
    )
  }

  // Resetear el estado `selectedItems` cuando el modal se abre
  useEffect(() => {
    if (isOpen) {
      if (editMode && extendedProps?.orderOriginIds) {
        setSelectedItems([...extendedProps.orderOriginIds])
        setOverloadPercentage(extendedProps.overloadPercentage)
        const selectedTypePriorization = []

        if (extendedProps.hasPriorityCourier) {
          selectedTypePriorization.push(2)
        }

        if (extendedProps.hasPrioritySingleSku) {
          selectedTypePriorization.push(1)
        }
        setSelectedTypePriorization(selectedTypePriorization)
      } else {
        setSelectedItems([]) // En modo creación, debe empezar vacío
        setOverloadPercentage(0)
        setSelectedTypePriorization([])
      }

      requestGetTypeOperation(
        extendedProps?.typeStorageId || initialValues.typeStorageId,
        extendedProps?.productOriginId
      )
    } else {
      setSelectedWarehousesByOperation([])
    }
  }, [isOpen, editMode, extendedProps])

  useEffect(() => {
    if (stateTypeOperation.data && stateTypeOperation.data.length > 0) {
      setSelectedWarehousesByOperation(
        stateTypeOperation.data.map((op) => ({
          typeOperationId: op.typeOperationId,
          warehouseIds: op.warehouses.map((w) => w.warehouseId)
        }))
      )
    }
  }, [stateTypeOperation.data])

  const requestProductOriginPatch = (values: ProductOrigin) => {
    patchOriginCallApi(patchProductOrigin(values.productOriginId, values))
      .then(() => {
        getProductOrigins()
        handleCloseModalMessage(
          true,
          `Marca actualizada correctamente para marca ${values.name}`
        )
      })
      .catch((error) => {
        handleCloseModalMessage(false, error)
        throw error
      })
  }

  const requestProductOriginPost = (values: ProductOrigin) => {
    patchOriginCallApi(postProductOrigin(values))
      .then(() => {
        getProductOrigins()
        handleCloseModalMessage(
          true,
          `Marca creada correctamente para marca ${values.name}`
        )
      })
      .catch((error) => {
        handleCloseModalMessage(false, error)
        throw error
      })
  }

  const handleSetData = (values: ProductOrigin) => {
    const dataToSend: ProductOrigin = {
      ...values,
      orderOriginIds: selectedItems,
      hasPriorityCourier:
        selectedTypePriorization.find((el) => el === 2) !== undefined,
      hasPrioritySingleSku:
        selectedTypePriorization.find((el) => el === 1) !== undefined,
      overloadPercentage,
      warehousesByOperation: selectedWarehousesByOperation
    }

    return editMode
      ? requestProductOriginPatch(dataToSend)
      : requestProductOriginPost(dataToSend)
  }

  const handleInputChangeNumericNotNegative = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (['e', 'E', '+', '-', '.'].includes(e.key)) {
      e.preventDefault()
    }
  }

  const handleSelectedWarehouses = (
    typeOperationId: number,
    newSelected: number[]
  ) => {
    setSelectedWarehousesByOperation((prev) => {
      if (!prev) return [{ typeOperationId, warehouseIds: newSelected }]

      const exists = prev.find((op) => op.typeOperationId === typeOperationId)

      if (exists) {
        return prev.map((op) =>
          op.typeOperationId === typeOperationId
            ? { ...op, warehouseIds: newSelected }
            : op
        )
      } else {
        return [...prev, { typeOperationId, warehouseIds: newSelected }]
      }
    })
  }

  const isDisableButton = (isSubmitting: boolean, isValid: boolean) => {
    return (
      isSubmitting ||
      !isValid ||
      stateTypeOperation.status === Status.Loading ||
      status === Status.Loading
    )
  }

  return (
    <Fragment>
      <Modal
        open={isOpenModalConfirm}
        title={'titulo'}
        onClose={openModalConfirm}
        maxWidth={sizeModalConfirm}
        cancelButton={{
          action: closeModalConfirm,
          title: 'Cancelar'
        }}
        successButton={{
          action: () => alert('crear handle'),
          title: 'Guardar'
        }}
      ></Modal>
      <Modal
        loading={status === Status.Loading}
        open={isOpen}
        title={editMode ? 'Actualizar Marca' : 'Crear Marca'}
        onClose={closeModal}
        maxWidth={size}
      >
        <Fragment>
          <Formik
            initialValues={
              extendedProps !== undefined ? extendedProps : initialValues
            }
            onSubmit={async (values) => {
              await handleSetData(values)
            }}
            validationSchema={productOriginValidationSchema}
          >
            {({ isSubmitting, isValid, values }) => (
              <Form autoComplete='off'>
                <Grid container direction='row' spacing={2}>
                  <Grid item xs={6}>
                    <Field
                      fullWidth
                      name='origin'
                      component={TextField}
                      label='Origen'
                      variant='outlined'
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Field
                      fullWidth
                      name='name'
                      component={TextField}
                      label='Marca'
                      variant='outlined'
                    />
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <GenericMultiselectDLL<OrderOrigin>
                      initialValues={orderOrigins || []}
                      title={'Origen de Pedido'}
                      name={'origin'}
                      chipName={'origin'}
                      valueKey={'orderOriginId'}
                      selectedItems={selectedItems}
                      setSelectedItems={setSelectedItems}
                      fullWidth
                      variant='outlined'
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <GenericMultiselectDLL<{
                      typePriotizationId: number
                      name: string
                    }>
                      initialValues={[
                        { typePriotizationId: 1, name: 'Monosku' },
                        { typePriotizationId: 2, name: 'Servicio Courier' }
                      ]}
                      title={'Priorización'}
                      name={'name'}
                      chipName={'name'}
                      valueKey={'typePriotizationId'}
                      selectedItems={selectedTypePriorization}
                      setSelectedItems={setSelectedTypePriorization}
                      fullWidth
                      variant='outlined'
                    />
                  </Grid>

                  <Grid item xs={6} sm={6}>
                    <Grid container sm={12} spacing={2}>
                      <Grid item xs={12} sm={12}>
                        <Paper
                          variant='outlined'
                          style={{
                            width: '95%',
                            padding: '20px 10px'
                          }}
                        >
                          <Grid container alignItems='center' spacing={0}>
                            <Grid item xs={3}>
                              <Field
                                name='hasExpirationDays'
                                component={CheckboxWithLabel}
                                type='checkbox'
                                color='default'
                                Label={{ label: 'Activo' }}
                              />
                            </Grid>
                            <Grid item xs={9}>
                              <Field
                                fullWidth
                                name='expirationDays'
                                component={TextField}
                                label='Tolerancia en días de Picking FEFO'
                                variant='outlined'
                                type='number'
                                inputProps={{
                                  min: 0
                                }}
                                onKeyDown={handleInputChangeNumericNotNegative}
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Field name='typeStorageId'>
                          {({ field, meta }: any) => (
                            <Box mb={2}>
                              <TypeStorageDLL
                                value={field.value || 0}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  field.onChange(e)
                                  const newValue = e.target.value

                                  requestGetTypeOperation(
                                    Number(newValue),
                                    extendedProps?.productOriginId
                                  )
                                }}
                                fullWidth={true}
                                type='outlined'
                                defaultLabel='Tipo Almacenamiento'
                              />

                              {meta.touched && meta.error && (
                                <div style={{ color: 'red' }}>{meta.error}</div>
                              )}
                            </Box>
                          )}
                        </Field>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid container item xs={12} sm={6}>
                    <Paper
                      variant='outlined'
                      style={{ width: '100%', padding: 10 }}
                    >
                      <Grid container spacing={2}>
                        {/* Primera fila: Checkbox + textos */}
                        <Grid
                          container
                          item
                          xs={12}
                          alignItems='center'
                          spacing={2}
                        >
                          <Grid item>
                            <Field
                              name='hasPriorityOverload'
                              component={CheckboxWithLabel}
                              type='checkbox'
                              color='default'
                              Label={{ label: 'Activo' }}
                            />
                          </Grid>
                          <Grid item>
                            <Typography
                              style={{ fontWeight: 500, marginBottom: 16 }}
                            >
                              Priorización por Sobrecarga de Pedido
                            </Typography>
                          </Grid>
                        </Grid>

                        {/* Segunda fila: Slider */}
                        <Grid item xs={12}>
                          <Box px={2}>
                            <Slider
                              disabled={!values.hasPriorityOverload}
                              value={overloadPercentage}
                              defaultValue={30}
                              getAriaValueText={(value) => `${value}`}
                              aria-labelledby='discrete-slider'
                              valueLabelDisplay='on'
                              step={10}
                              min={10}
                              max={100}
                              onChange={(_, value) =>
                                setOverloadPercentage(value as number)
                              }
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
                <TypeOperationWarehousesTable
                  typeOperations={stateTypeOperation.data || []}
                  warehouses={warehouses || []}
                  isLoading={stateTypeOperation.status === Status.Loading}
                  selectedWarehousesByOperation={
                    selectedWarehousesByOperation || []
                  }
                  handleSelectedWarehouses={handleSelectedWarehouses}
                />
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <Button
                      disabled={isDisableButton(isSubmitting, isValid)}
                      fullWidth
                      type='submit'
                      variant='contained'
                      color={isValid ? 'primary' : 'secondary'}
                      style={{
                        marginTop: '25px'
                      }}
                      startIcon={
                        status === Status.Loading ? (
                          <CircularProgress size='0.9rem' />
                        ) : undefined
                      }
                    >
                      {status === Status.Loading ? '' : 'Guardar'}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Fragment>
      </Modal>
    </Fragment>
  )
}
